import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from '@hooks/index';
import { formatDate } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { COLON, EMPTY_VALUE } from '../constants';
import { IAddress } from '../types';
import { formatCityState } from '../utils';

// ============================================================================
// Helpers
// ============================================================================

const renderAddress = ({ addressLine1, addressLine2, city, state }: IAddress): React.ReactNode => (
    <>
        {addressLine1 || EMPTY_VALUE}<br />
        {addressLine2 && <>{addressLine2}<br /></>}
        {formatCityState(city, state)}
    </>
);

// ============================================================================
// Component
// ============================================================================

function HistoricalBasicInfoPage() {
    const { historicalApplicationsBasicInfo } = useAppSelector(state => state.historicalApplications);
    const {
        firstName,
        lastName,
        dob,
        addressLine1,
        addressLine2,
        city,
        state,
        zipCode,
        emailId,
        phoneNumber,
        ssnLastFour,
        preferredContactMethod,
    } = historicalApplicationsBasicInfo;

    return (
        <div className="padding-3" data-testid="historical-basic-info-container">
            <div className="padding-bottom-2 margin-bottom-3 margin-top-1">
                <h4 className="margin-0">
                    <FormattedMessage id="admin.member.view.side.nav.basic_information" />
                </h4>
            </div>

            <div className="historical-basic-info">
                <table data-testid="historical-basic-info-table">
                    <tbody>
                        <tr>
                            <td className="label"><FormattedMessage id="member.enrollment_list.premiumhistory.enrollee.name" />{COLON}</td>
                            <td data-testid="historical-name">{`${firstName || ''} ${lastName || ''}`.trim() || EMPTY_VALUE}</td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="historical.member.applications.basicInfo.dob" />{COLON}</td>
                            <td data-testid="historical-dob">{dob ? formatDate(dob, MM_SLASH_DD_SLASH_YYYY) : EMPTY_VALUE}</td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="historical.member.applications.basicInfo.address" />{COLON}</td>
                            <td data-testid="historical-address">
                                {renderAddress({ addressLine1, addressLine2, city, state })}
                            </td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="historical.member.applications.basicInfo.zipCode" />{COLON}</td>
                            <td data-testid="historical-zip">{zipCode || EMPTY_VALUE}</td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="historical.member.applications.basicInfo.email" />{COLON}</td>
                            <td data-testid="historical-email">{emailId || EMPTY_VALUE}</td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="historical.member.applications.basicInfo.phone" />{COLON}</td>
                            <td data-testid="historical-phone">{phoneNumber || EMPTY_VALUE}</td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="member.enrollment_list.premiumhistory.enrollee.ssn" />{COLON}</td>
                            <td data-testid="historical-ssn">{socialSecurityNumberTools.maskSsnLastFour(ssnLastFour)}</td>
                        </tr>
                        <tr>
                            <td className="label"><FormattedMessage id="historical.member.applications.basicInfo.preferredContactMethod" />{COLON}</td>
                            <td data-testid="historical-preferred-contact-method">{preferredContactMethod || EMPTY_VALUE}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default HistoricalBasicInfoPage;

